<!-- eslint-disable max-len -->
<template>
    <teleport to="head">
        <title>{{ HEAD_TITLE }} | Política de Privacidad</title>
    </teleport>
    <UnauthNav />
    <div v-if="user.id">
        <MobileControls />
    </div>
    <IphoneBar />
    <div class="lcu-nonDash-wrap" :class="{ 'lcu-logged-dashWrap': user.id }">
        <SecondaryNav />
        <div class="lcu-page">
            <section class="section">
                <header class="lcu-section-header lcu-center-text">
                Política de Privacidad de Legacy Christian University
                </header>
            </section>
            <section class="section">
                <p>
                    Gracias por unirse a Legacy Christian University. En Legacy Christian University
                    respetamos su privacidad y queremos que usted comprenda cómo recopilamos,
                    usamos y compartimos datos sobre usted. Esta Política de Privacidad cubre nuestras
                    prácticas de recopilación de datos y describe sus derechos con respecto a sus datos
                    personales.
                </p>
                <p>
                    Esta Política de Privacidad se aplica cuando usted visita o utiliza los sitios web,
                    aplicaciones móviles, APIs o servicios relacionados de Legacy Christian University.
                    Al utilizar los Servicios, usted acepta los términos de esta Política de Privacidad. Usted
                    no puede utilizar los Servicios si no está de acuerdo con esta Política de Privacidad o
                    con cualquier otro acuerdo que rija su uso de los Servicios.
                </p>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Qué Datos Obtenemos</h2>
                <div class="lcu-term-section">
                    <p>
                        <strong>Datos que Usted Nos Proporciona</strong>
                    </p>
                    <p>
                        Podemos recopilar distintos datos de usted o sobre usted dependiendo de cómo
                        utilice los Servicios. A continuación se presentan algunos ejemplos para ayudarle a
                        comprender mejor los datos que recopilamos.
                    </p>
                    <p>
                        Cuando usted se inscribe con nosotros, crea una cuenta y utiliza los Servicios, incluso a
                        través de una plataforma de terceros, recopilamos cualquier dato que usted nos
                        proporcione directamente, incluyendo:
                    </p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            <strong>Datos de la Cuenta</strong> - Para utilizar ciertas funciones (como acceder
                            a contenido), usted necesita crear una
                            cuenta de usuario, lo cual requiere que recopilemos y almacenemos su dirección de
                            correo electrónico, contraseña y configuración de la cuenta. A medida que utilice
                            ciertas funciones del sitio, es posible que se le solicite proporcionar información
                            adicional. Por ejemplo, usted puede optar por proporcionar su número de teléfono
                            para mensajes promocionales y fines de autenticación, o información relacionada con
                            sus intereses de aprendizaje, incluyendo ocupación e intereses de habilidades. Al crear
                            la cuenta, le asignamos un número identificador único.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Datos del Perfil</strong> - Usted también puede optar por proporcionar
                            información de perfil como una fotografía, idioma, país u otros datos. Sus Datos
                            de Perfil pueden ser visibles públicamente para otros usuarios.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Datos de Aprendizaje</strong> - Cuando usted accede al contenido, recopilamos
                            ciertos datos, incluyendo qué
                            programas, tareas, laboratorios, espacios de trabajo y cuestionarios ha iniciado y
                            completado; contenido; certificados de finalización; insignias; sus intercambios con
                            instructores, asistentes de enseñanza y otros estudiantes; y ensayos, respuestas a
                            preguntas y otros elementos enviados para cumplir con los requisitos del programa y
                            del contenido relacionado.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Datos de Pago del Estudiante</strong> - Si usted realiza compras, recopilamos
                            ciertos datos sobre su compra (como su nombre,
                            dirección de facturación y código postal) según sea necesario para procesar su pedido
                            y que pueden guardarse opcionalmente para procesar pedidos futuros. Usted debe
                            proporcionar ciertos datos de pago y facturación directamente a nuestros proveedores
                            de servicios de pago, incluyendo su nombre, información de tarjeta de crédito,
                            dirección de facturación y código postal. También podemos recibir información
                            limitada, como el hecho de que usted tiene una nueva tarjeta y los últimos cuatro
                            dígitos de esa tarjeta, de los proveedores de servicios de pago para facilitar los pagos.
                            Por razones de seguridad, Legacy Christian University no recopila ni almacena datos
                            sensibles del titular de la tarjeta, como números completos de tarjetas de crédito o
                            datos de autenticación de tarjetas.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Promociones y Encuestas</strong> - Podemos invitarle a completar una encuesta
                            o participar en una promoción (como un
                            concurso, sorteo o desafío), ya sea a través de los Servicios o de una plataforma de
                            terceros. Si usted participa, recopilaremos y almacenaremos los datos que proporcione
                            como parte de su participación, tales como su nombre, dirección de correo
                            electrónico, dirección postal, fecha de nacimiento o número de teléfono. Dichos datos
                            están sujetos a esta Política de Privacidad, a menos que se indique lo contrario en las
                            reglas oficiales de la promoción o en otra política de privacidad. Los datos recopilados
                            se utilizarán para administrar la promoción o encuesta, incluyendo la notificación de
                            ganadores y la distribución de premios. Para recibir un premio, puede ser necesario
                            que nos permita publicar públicamente parte de su información. Cuando utilizamos
                            una plataforma de terceros para administrar una encuesta o promoción, se aplicará la
                            política de privacidad de dicho tercero.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Comunicaciones y Soporte</strong> - Si usted se comunica con nosotros para
                            recibir soporte o para reportar un problema o
                            inquietud (independientemente de si ha creado una cuenta), recopilamos y
                            almacenamos su información de contacto, mensajes y otros datos sobre usted, como
                            su nombre, dirección de correo electrónico, ubicación, ID de usuario y cualquier otro
                            dato que proporcione o que recopilemos por medios automatizados (descritos más
                            adelante). Utilizamos estos datos para responderle e investigar su consulta o inquietud.
                        </li>
                    </ul>
                    <p>
                        Los datos enumerados anteriormente son almacenados por nosotros y se asocian con
                        su cuenta. El uso y la transferencia por parte de Legacy Christian University a cualquier
                        otra aplicación de la información recibida de las APIs de Google cumplirán con la
                        Política de Datos de Usuario de los Servicios de Google API, incluidos los requisitos de
                        Uso Limitado.
                    </p>
                    <p>
                        <strong>Datos que Recopilamos por Medios Automatizados</strong>
                    </p>
                    <p>
                        Cuando usted accede a los Servicios (incluyendo la navegación de contenido),
                        recopilamos ciertos datos por medios automatizados, incluyendo:
                    </p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            <strong>Datos del Sistema</strong> - Datos técnicos sobre su computadora o
                            dispositivo, como su dirección IP; tipo de
                            dispositivo; tipo y versión del sistema operativo; identificadores únicos del dispositivo,
                            incluidos identificadores de publicidad móvil; navegador; idioma del navegador;
                            dominio y otros datos del sistema; y tipos de plataforma.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Datos de Uso</strong> - Estadísticas de uso sobre sus interacciones con los
                            Servicios, incluyendo contenido
                            accedido, tiempo dedicado a páginas o al Servicio, páginas visitadas, funciones
                            utilizadas, consultas de búsqueda, datos de clics, fecha y hora, referente y otros datos
                            relacionados con su uso de los Servicios.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Datos Geográficos Aproximados</strong> - Ubicación geográfica aproximada, incluyendo
                            información como país, ciudad y
                            coordenadas geográficas, calculadas en función de su dirección IP.
                        </li>
                    </ul>
                    <p>
                        Los datos anteriores (“Datos de Cookies”) se recopilan mediante el uso de archivos de
                        registro del servidor y tecnologías de seguimiento, como se detalla en la sección
                        “Cookies y Herramientas de Recopilación de Datos” a continuación. Se almacenan por
                        nosotros y se asocian con su cuenta. Dependiendo de su configuración de cookies y
                        navegador, estos datos también pueden transmitirse a Google con fines publicitarios,
                        como se describe con mayor detalle más adelante.
                    </p>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Cómo Obtenemos Datos Sobre Usted</h2>
                <div class="lcu-term-section">
                    <p>
                        <strong>Cookies y Herramientas de Recopilación de Datos</strong>
                    </p>
                    <p>
                        Utilizamos cookies, que son pequeños archivos de texto almacenados por su
                        navegador, para recopilar, almacenar y compartir datos sobre sus actividades en sitios
                        web, incluyendo Legacy Christian University. Las cookies nos permiten recordar
                        aspectos de sus visitas, como su idioma preferido, y hacer que el sitio sea más fácil de
                        usar. Para obtener más información sobre cookies, visite:
                        <a href="https://cookiepedia.co.uk/all-about-cookies" rel="noopener noreferrer" target="_blank" class="lcu-term-link">
                            https://cookiepedia.co.uk/all-about-cookies
                        </a>.
                    </p>
                    <p>
                        Legacy Christian University y los proveedores de servicios que actúan en nuestro
                        nombre (como Google Analytics y anunciantes de terceros) utilizan archivos de registro
                        del servidor y herramientas automatizadas de recopilación de datos como cookies,
                        etiquetas, scripts, enlaces personalizados, huellas digitales del dispositivo o navegador
                        y balizas web (en conjunto, “Herramientas de Recopilación de Datos”) cuando usted
                        accede y utiliza los Servicios. Estas herramientas rastrean y recopilan automáticamente
                        ciertos Datos del Sistema, Datos de Uso y Datos Geográficos Aproximados cuando
                        usted utiliza los Servicios. En algunos casos, vinculamos los datos recopilados
                        mediante estas herramientas con otros datos que recopilamos según se describe en
                        esta Política de Privacidad.
                    </p>
                    <p>
                        <strong>Por Qué Usamos Herramientas de Recopilación de Datos</strong>
                    </p>
                    <p>
                        Legacy Christian University utiliza los siguientes tipos de Herramientas de Recopilación
                        de Datos para los fines descritos:
                    </p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            <strong>Estrictamente Necesarias</strong>: Permiten acceder al sitio, proporcionar
                            funciones básicas (como iniciar sesión o
                            acceder a contenido), proteger el sitio, prevenir inicios de sesión fraudulentos y
                            detectar y prevenir abusos o usos no autorizados de su cuenta. Son necesarias para
                            que los Servicios funcionen correctamente.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Funcionales</strong>: Recuerdan datos sobre su navegador y preferencias,
                            proporcionan funcionalidades
                            adicionales, personalizan el contenido y recuerdan configuraciones que afectan la
                            apariencia y el comportamiento de los Servicios.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Rendimiento</strong>: Ayudan a medir y mejorar los Servicios proporcionando
                            datos de uso y rendimiento, conteos de visitas, fuentes de tráfico y pruebas de versiones del sitio.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Publicidad</strong>: These Data Collection Tools are used
                            to deliver relevant ads (on the site and/or other sites) based on things
                            we know about you like your Usage and System Data (as detailed in
                            the "What Data We Get" section), and things that the ad service
                            providers know about you
                            based on their tracking data. The ads can be based on your recent
                            activity or activity over time and across other sites and services.
                            To help deliver tailored advertising, we may provide these service
                            providers with a hashed, anonymized version of your email address
                            (in a non-human-readable form) and content that you share publicly
                            on the Services.
                        </li>
                        <li class="lcu-acc-bullet">
                            <strong>Redes Sociales</strong>: These Data Collection Tools enable
                            social media functionality, like sharing content with friends and
                            networks. These cookies may track a user or device across othe
                             sites and build a profile of user interests for targeted
                             advertising purposes.
                        </li>
                    </ul>
                    <p>
                        Permiten funcionalidades sociales como compartir contenido y pueden rastrear
                        usuarios o dispositivos para fines publicitarios.
                        Usted puede configurar su navegador para alertarle sobre cookies, limitar su uso o
                        rechazarlas. Si lo hace, es posible que algunas funciones de los Servicios no estén
                        disponibles.
                    </p>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Para Qué Usamos Sus Datos</h2>
                <div class="lcu-term-section">
                    <p>Utilizamos los datos recopilados para, entre otros fines:</p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            Proveer y administrar los Servicios
                        </li>
                        <li class="lcu-acc-bullet">
                            Procesar solicitudes y pagos
                        </li>
                        <li class="lcu-acc-bullet">
                            Comunicarnos con usted
                        </li>
                        <li class="lcu-acc-bullet">
                            Administrar y personalizar su cuenta
                        </li>
                        <li class="lcu-acc-bullet">
                            Garantizar el funcionamiento técnico y la seguridad
                        </li>
                        <li class="lcu-acc-bullet">
                            Solicitar retroalimentación
                        </li>
                        <li class="lcu-acc-bullet">
                            Marketing y promociones
                        </li>
                        <li class="lcu-acc-bullet">
                            Analizar tendencias y mejorar los Servicios
                        </li>
                        <li class="lcu-acc-bullet">
                            Cumplir con obligaciones legales
                        </li>
                        <li class="lcu-acc-bullet">
                            Proteger la seguridad e integridad de usuarios y Servicios
                        </li>
                    </ul>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Con Quién Compartimos Sus Datos</h2>
                <div class="lcu-term-section">
                    <p>
                        Podemos compartir sus datos con:
                    </p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            Instructores y asistentes
                        </li>
                        <li class="lcu-acc-bullet">
                            Otros estudiantes (según configuración)
                        </li>
                        <li class="lcu-acc-bullet">
                            Proveedores de servicios y contratistas
                        </li>
                        <li class="lcu-acc-bullet">
                            Socios comerciales
                        </li>
                        <li class="lcu-acc-bullet">
                            Servicios de análisis y enriquecimiento de datos
                        </li>
                        <li class="lcu-acc-bullet">
                            Plataformas de redes sociales
                        </li>
                        <li class="lcu-acc-bullet">
                            Administradores de promociones y encuestas
                        </li>
                        <li class="lcu-acc-bullet">
                            Anunciantes
                        </li>
                        <li class="lcu-acc-bullet">
                            Autoridades legales y de seguridad
                        </li>
                        <li class="lcu-acc-bullet">
                            Sucesores comerciales en caso de fusión, adquisición o venta de activos
                        </li>
                    </ul>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Seguridad</h2>
                <div class="lcu-term-section">
                    <p>
                        Legacy Christian University adopta medidas de seguridad apropiadas para proteger
                        sus datos. Sin embargo, ningún sistema es 100% seguro. Usted es responsable de
                        proteger su contraseña.
                    </p>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Sus Derechos</h2>
                <div class="lcu-term-section">
                    <p>
                       Usted puede:
                    </p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            Elegir qué datos proporcionar
                        </li>
                        <li class="lcu-acc-bullet">
                            Administrar cookies y publicidad
                        </li>
                        <li class="lcu-acc-bullet">
                            Acceder, actualizar o eliminar sus datos
                        </li>
                        <li class="lcu-acc-bullet">
                            Solicitar acceso a su información personal
                        </li>
                    </ul>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Política Sobre Menores</h2>
                <div class="lcu-term-section">
                    <p>
                        Los menores de la edad legal requerida no pueden usar los Servicios. Si detectamos
                        datos de menores, tomaremos medidas para eliminarlos.
                    </p>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Reglas por Jurisdicción</h2>
                <div class="lcu-term-section">
                    <p>
                       Se incluyen derechos específicos para usuarios de:
                    </p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            California (CCPA)
                        </li>
                        <li class="lcu-acc-bullet">
                            Nevada
                        </li>
                        <li class="lcu-acc-bullet">
                            Australia
                        </li>
                        <li class="lcu-acc-bullet">
                            Unión Europea, Reino Unido y Suiza (GDPR y DPF)
                        </li>
                    </ul>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Marco de Privacidad de Datos</h2>
                <div class="lcu-term-section">
                    <p>
                       Legacy Christian University cumple con:
                    </p>
                    <ul>
                        <li class="lcu-acc-bullet">
                            EU-U.S. Data Privacy Framework
                        </li>
                        <li class="lcu-acc-bullet">
                            UK Extension
                        </li>
                        <li class="lcu-acc-bullet">
                            Swiss-U.S. Data Privacy Framework
                        </li>
                    </ul>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Usuarios Fuera de EE. UU.</h2>
                <div class="lcu-term-section">
                    <p>
                        Usted consiente la transferencia y procesamiento de sus datos en Estados Unidos y
                        otros países.
                    </p>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Actualizaciones y Contacto</h2>
                <div class="lcu-term-section">
                    <p>
                        Podemos modificar esta Política de Privacidad. El uso continuado de los Servicios
                        implica aceptación de los cambios.
                    </p>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Interpretación</h2>
                <div class="lcu-term-section">
                    <p>
                        Los términos en mayúscula no definidos aquí se interpretan conforme a los Términos
                        de Uso. En caso de conflicto, prevalece la versión en inglés.
                    </p>
                </div>
            </section>
            <section class="section">
                <h2 class="lcu-term-header">Preguntas</h2>
                <div class="lcu-term-section">
                    <p>
                        Si tiene preguntas o inquietudes sobre esta Política de Privacidad, puede comunicarse
                        con nuestro equipo de privacidad.
                    </p>
                </div>
            </section>
        </div>
        <Footer />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Footer from 'ç/footer/Footer.vue';
import SecondaryNav from 'ç/nav/SecondaryNav.vue';
import MobileControls from 'ç/nav/MobileControls.vue';
import IphoneBar from 'ç/nav/iphone-bar/IphoneBar.vue';
import UnauthNav from 'ç/nav/UnauthNav.vue';

export default {
  name: 'privacy-policy-page',
  computed: {
    ...mapGetters(['user']),
  },
  components: {
    UnauthNav,
    Footer,
    MobileControls,
    IphoneBar,
    SecondaryNav,
  },
};
</script>

<style scoped lang="scss">
.lcu-acc-bullet {
  margin-bottom: 15px;
}

.lcu-term-header {
  font-size: 18px;
  font-weight: bold;
}

.lcu-term-section {
  padding-left: 25px;

  @media only screen and (max-width: $mobile-max) {
   padding-left: 0;
  }
}

.lcu-term-link {
  text-decoration: underline;
  color: $legacy-secondary;

  &:hover {
    text-decoration: none;
  }
}
</style>
